import { Dropdown, DropdownOption } from '@/components/dropdown'
import { Controller, Path, useFormContext } from 'react-hook-form'
import { components, ControlProps, MultiValue } from 'react-select'
import { getFilterDropdownStyles } from '@/modules/salary/filters/desktop/utils'
import { FormControl } from '@/components/form-control'
import { cn } from '@/lib/css-utils'
import { useIsMobile } from '@/utils/screen'
import { MapPin } from 'lucide-react'
import { useStateSearch } from '@/modules/salary/filters/use-state-search'
import {
  getStateOptions,
  stateAlphabeticalSorter,
} from '@/modules/salary/filters/utils'

interface LocationSelectProps<T extends Record<string, unknown>> {
  name: Path<T>
  onSelectLocation?: (location: DropdownOption | null) => void
  className?: string
  label?: string
}

function ControlContainer({ children, ...props }: ControlProps) {
  return (
    <components.Control {...props} className="border-r-0">
      <MapPin className="h-4 w-4 text-select-icon-text ml-2 react-select__icon" />
      {children}
    </components.Control>
  )
}

const customComponents = {
  Control: ControlContainer,
  DropdownIndicator: null,
  IndicatorSeparator: null,
}

export function LocationSelect<T extends Record<string, unknown>>({
  name,
  className,
  label,
  onSelectLocation,
}: LocationSelectProps<T>) {
  const isMobile = useIsMobile()
  const { control } = useFormContext<T>()
  const { searchState, states } = useStateSearch()
  const defaultOptions = getStateOptions(states).sort(stateAlphabeticalSorter)

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, value, onChange }, fieldState }) => (
        <FormControl
          errorText={fieldState.error?.message}
          className={cn('max-w-[10rem] lg:max-w-[11rem] h-full', className)}
          label={label}
        >
          <Dropdown
            loadOptions={searchState}
            defaultOptions={defaultOptions}
            onChange={(state: MultiValue<DropdownOption> | null) => {
              onChange(state?.[0] ?? null)
              onSelectLocation?.(state?.[0] ?? null)
            }}
            value={value as DropdownOption | null}
            menuPortalTarget={null}
            placeholder="United States"
            id="state-selector"
            isClearable
            components={customComponents}
            styles={{
              ...getFilterDropdownStyles({ isMobile }),
              placeholder: (base: Record<string, unknown>) => ({
                ...base,
                color: 'var(--color-input-text) !important',
              }),
            }}
            ref={ref}
          />
        </FormControl>
      )}
    />
  )
}
